import { ReactComponent as BrandLogo } from "./assets/brand-logo-blue.svg";
import { ReactComponent as BrandText } from "./assets/brand-text-blue.svg";
import { ReactComponent as CloseMobileMenuIcon } from "./assets/mobile-menu-close.inline.svg";
import { ReactComponent as MobileMenuIcon } from "./assets/mobile-menu.inline.svg";
import { navbarContentEn } from "./content.en";
import styles from "./styles.module.css";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";

const SCROLL_TOP_THRESHOLD = 90;
const MOBILE_BREAKPOINT = 1024;

const ClutchWidget: React.FC = () => {
  useEffect(() => {
    const scriptId = "clutch-widget-script";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://widget.clutch.co/static/js/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div
      className={`${styles.clutchWidget} clutch-widget`}
      data-url="https://widget.clutch.co"
      data-widget-type="2"
      data-height="45"
      data-nofollow="true"
      data-expandifr="true"
      data-clutchcompany-id="25248"
    />
  );
};

const DesignRushWidget: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!ref.current) {
        return;
      }

      const url = new URL(
        "https://www.designrush.com/api/widgets/agency-reviews",
      );
      url.searchParams.set("agency_id", "28070");
      url.searchParams.set("style", "light");

      const response = await fetch(url.toString());
      if (!response.ok || !ref.current) {
        return;
      }

      const html = await response.text();
      ref.current.innerHTML = html;
    };

    void load();
  }, []);

  return <div ref={ref} className={styles.designRushWidget} />;
};

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesTimeoutRef = useRef<number | null>(null);
  const lastScrollYRef = useRef(0);
  const rootRef = useRef<HTMLElement | null>(null);

  const servicesItem = navbarContentEn.primaryLinks[0];
  const primaryLinks = useMemo(
    () =>
      navbarContentEn.primaryLinks.filter(
        (link) =>
          link.href !== servicesItem.href && link.label !== servicesItem.label,
      ),
    [servicesItem.href, servicesItem.label],
  );

  useEffect(() => {
    const onScroll = (): void => {
      const scrollY = window.scrollY;
      const isScrollingDown = scrollY > lastScrollYRef.current;
      const isScrollingUp = scrollY < lastScrollYRef.current;

      setHasScrolled(scrollY > SCROLL_TOP_THRESHOLD);

      if (isServicesOpen || isMobileMenuOpen) {
        setIsHeaderVisible(true);
      } else if (isScrollingDown && scrollY > SCROLL_TOP_THRESHOLD) {
        setIsHeaderVisible(false);
      } else if (isScrollingUp) {
        setIsHeaderVisible(true);
      }

      lastScrollYRef.current = scrollY;
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobileMenuOpen, isServicesOpen]);

  useEffect(() => {
    if (!isServicesOpen) {
      return;
    }

    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [isServicesOpen]);

  useEffect(() => {
    const onResize = (): void => {
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const clearServicesTimeout = (): void => {
    if (servicesTimeoutRef.current) {
      window.clearTimeout(servicesTimeoutRef.current);
      servicesTimeoutRef.current = null;
    }
  };

  const openServices = (): void => {
    clearServicesTimeout();
    setIsServicesOpen(true);
  };

  const closeServicesWithDelay = (): void => {
    clearServicesTimeout();
    servicesTimeoutRef.current = window.setTimeout(() => {
      setIsServicesOpen(false);
    }, 150);
  };

  const toggleMobileMenu = (): void => {
    setMobileMenuOpen((prev) => !prev);
    setIsServicesOpen(false);
    setIsHeaderVisible(true);
  };

  const toggleMobileServices = (): void => {
    setIsServicesOpen((prev) => !prev);
  };

  return (
    <header
      ref={rootRef}
      className={classNames(styles.navbar, {
        [styles.mobileNavbar]: isMobileMenuOpen,
        [styles.dropdownActive]: isServicesOpen,
        [styles.headerHidden]: !isHeaderVisible,
      })}
    >
      <div
        className={classNames(styles.navbarContainer, {
          [styles.scrolled]: hasScrolled,
        })}
      >
        <a className={styles.brandLink} href={navbarContentEn.brandHref}>
          <div className={styles.brand}>
            <BrandLogo className={styles.brandSymbol} />
            <div className={styles.brandTypeContainer}>
              <BrandText />
            </div>
          </div>
        </a>

        <div className={styles.desktopNavMenu}>
          <nav>
            <ul className={styles.navLinks}>
              <li className={styles.navlink}>
                <div className={styles.desktopDropdownButton}>
                  <div
                    className={styles.dropdownContainer}
                    onMouseEnter={openServices}
                    onMouseLeave={closeServicesWithDelay}
                  >
                    <a href={servicesItem.href} className={styles.link}>
                      <span>{servicesItem.label}</span>
                    </a>
                    <button
                      type="button"
                      className={styles.dropdownArrow}
                      data-active={isServicesOpen ? "" : undefined}
                    >
                      <span className={styles.arrowIcon}>{">"}</span>
                    </button>
                  </div>
                </div>
                <div className={styles.mobileDropdownButton}>
                  <div className={styles.dropdownContainer}>
                    <a href={servicesItem.href} className={styles.link}>
                      <span>{servicesItem.label}</span>
                    </a>
                    <button
                      type="button"
                      className={styles.dropdownArrow}
                      data-active={isServicesOpen ? "" : undefined}
                      onClick={toggleMobileServices}
                    >
                      <span className={styles.arrowIcon}>{">"}</span>
                    </button>
                  </div>
                </div>
              </li>

              {primaryLinks.map((link) => (
                <li key={link.label} className={styles.navlink}>
                  <a href={link.href} className={styles.link}>
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}

              <li className={styles.navlink}>
                <a
                  href={navbarContentEn.cta.href}
                  className={classNames(styles.link, styles.outlineLink)}
                >
                  <span>{navbarContentEn.cta.label}</span>
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <button
          className={classNames(styles.mobileMenuButton, {
            [styles.active]: isMobileMenuOpen,
          })}
          onClick={toggleMobileMenu}
          type="button"
        >
          {isMobileMenuOpen ? <CloseMobileMenuIcon /> : <MobileMenuIcon />}
        </button>
      </div>

      <div
        className={classNames(styles.servicesDrop, {
          [styles.servicesDropOpen]: isServicesOpen,
        })}
        data-active={isServicesOpen ? "" : undefined}
        onMouseEnter={openServices}
        onMouseLeave={closeServicesWithDelay}
      >
        <div className={styles.servicesDropContainer}>
          <div className={styles.widges}>
            <ClutchWidget />
            <DesignRushWidget />
          </div>

          <nav>
            {navbarContentEn.servicesDropdown.map((section) => (
              <div className={styles.block} key={section.title}>
                <h5>{section.title}</h5>
                <ul>
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <a href={link.href}>{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </div>

      <div
        className={classNames(styles.mobileNavMenu, {
          [styles.active]: isMobileMenuOpen,
        })}
      >
        <nav>
          <ul className={styles.navLinks}>
            <li className={styles.navlink}>
              <div className={styles.dropdownContainer}>
                <a href={servicesItem.href} className={styles.link}>
                  <span>{servicesItem.label}</span>
                </a>
                <button
                  type="button"
                  className={styles.dropdownArrow}
                  data-active={isServicesOpen ? "" : undefined}
                  onClick={toggleMobileServices}
                >
                  <span className={styles.arrowIcon}>{">"}</span>
                </button>
              </div>
            </li>

            {primaryLinks.map((link) => (
              <li key={link.label} className={styles.navlink}>
                <a href={link.href} className={styles.link}>
                  <span>{link.label}</span>
                </a>
              </li>
            ))}

            <li className={styles.navlink}>
              <a
                href={navbarContentEn.cta.href}
                className={classNames(styles.link, styles.outlineLink)}
              >
                <span>{navbarContentEn.cta.label}</span>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};
